import React from 'react';
import { Drawer, Form, Input, InputNumber, Select, Switch, Button, DatePicker, Row, Col, Popconfirm } from 'antd';
import { Archive, Trash2, History } from 'lucide-react';
import { fmtVZ } from '../../../utils/timeUtils';
import AuditImageThumb from './AuditImageThumb';
import AuditStatusBadge from './AuditStatusBadge';
import { BarcodeIcon } from './AuditFlagChips';
import { useAuditProductForm } from './auditDrawers.controller';

/** Group heading inside the form. */
const FormGroup = ({ title, children }) => (
    <div className="mb-2">
        <div className="mb-3 text-[13px] font-semibold text-surface-900">{title}</div>
        {children}
    </div>
);

export default function AuditProductDrawer({ open, onClose, product, onSave, saving, onRecordClick, onDelete, onForceDelete, deleting }) {
    const { form, handleFinish, recentScans } = useAuditProductForm({ open, product, onSave });

    return (
        <Drawer
            title="Editar producto de auditoría"
            width={600}
            open={open}
            onClose={onClose}
            destroyOnClose
            footer={
                <div className="flex w-full items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                        <Popconfirm
                            title="¿Archivar este producto?"
                            description="Se desactivará del catálogo. Los escaneos existentes se conservan."
                            onConfirm={() => onDelete?.(product)}
                            okText="Sí, archivar"
                            okType="danger"
                            cancelText="Cancelar"
                            getPopupContainer={() => document.body}>
                            <Button danger icon={<Archive />} loading={deleting} disabled={!product?._id}>
                                Archivar
                            </Button>
                        </Popconfirm>
                        <Popconfirm
                            title="¿Eliminar permanentemente?"
                            description="Desvincula escaneos y borra el producto del catálogo."
                            onConfirm={() => onForceDelete?.(product)}
                            okText="Sí, eliminar"
                            okType="danger"
                            cancelText="Cancelar"
                            getPopupContainer={() => document.body}>
                            <Button danger type="text" icon={<Trash2 />} loading={deleting} disabled={!product?._id}>
                                Eliminar
                            </Button>
                        </Popconfirm>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button onClick={onClose}>Cancelar</Button>
                        <Button type="primary" loading={saving} onClick={() => form.submit()}>
                            Guardar cambios
                        </Button>
                    </div>
                </div>
            }>
            <p className="m-0 mb-4 text-sm text-surface-500">
                Catálogo usado cuando los auditores escanean códigos. Los cambios afectan búsquedas y enriquecimiento en la app.
            </p>
            {product?.barcodeMeta && (
                <div className="mb-5 flex items-center gap-2 rounded-lg border border-highlight-border bg-highlight-bg px-3 py-2">
                    <BarcodeIcon barcodeMeta={product.barcodeMeta} hasCatalogMatch />
                    <span className="text-xs text-surface-600">
                        {product.barcodeMeta.format} — {product.barcodeMeta.valid ? 'válido' : product.barcodeMeta.warnings?.join(', ')}
                    </span>
                </div>
            )}
            <Form form={form} layout="vertical" onFinish={handleFinish}>
                <FormGroup title="Producto">
                    <Form.Item name="name" label="Nombre" rules={[{ required: true, message: 'Requerido' }]}>
                        <Input />
                    </Form.Item>
                    <Row gutter={16}>
                        <Col xs={24} sm={12}>
                            <Form.Item name="code" label="Código / barcode" rules={[{ required: true, message: 'Requerido' }]}>
                                <Input className="tabular-nums" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item name="country" label="País">
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item name="category" label="Categoría">
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item name="segment" label="Segmento">
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={8}>
                            <Form.Item name="grams" label="Gramos">
                                <InputNumber className="w-full" min={0} step={1} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={8}>
                            <Form.Item name="price" label="Precio (referencia)">
                                <InputNumber className="w-full" min={0} step={0.01} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={8}>
                            <Form.Item name="currency" label="Moneda">
                                <Input placeholder="dollars, bs, USD…" />
                            </Form.Item>
                        </Col>
                    </Row>
                </FormGroup>

                <div className="my-4 border-t border-surface-100" />

                <FormGroup title="Estado en catálogo">
                    <Row gutter={16}>
                        <Col xs={24} sm={12}>
                            <Form.Item name="status" label="Estado" rules={[{ required: true }]}>
                                <Select
                                    options={[
                                        { value: 'complete', label: 'Completo' },
                                        { value: 'pending_enrichment', label: 'Pendiente de ajuste' },
                                    ]}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={12} sm={6}>
                            <Form.Item name="active" label="Activo" valuePropName="checked">
                                <Switch />
                            </Form.Item>
                        </Col>
                        <Col xs={12} sm={6}>
                            <Form.Item name="missingData" label="Datos mínimos / faltantes" valuePropName="checked">
                                <Switch />
                            </Form.Item>
                        </Col>
                    </Row>
                </FormGroup>

                <div className="my-4 border-t border-surface-100" />

                <FormGroup title="Enriquecimiento">
                    <Row gutter={16}>
                        <Col xs={24} sm={12}>
                            <Form.Item name="enrichedAt" label="Enriquecido el">
                                <DatePicker showTime className="w-full" allowClear />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item name="enrichedBy" label="Enriquecido por (ID usuario)">
                                <Input placeholder="ObjectId opcional" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item name="tenantId" label="Tenant ID (opcional)">
                                <Input placeholder="ObjectId opcional" />
                            </Form.Item>
                        </Col>
                    </Row>
                </FormGroup>
            </Form>

            {recentScans.length > 0 && (
                <div className="mt-2 border-t border-surface-100 pt-5">
                    <div className="flex items-center gap-2 text-[15px] font-semibold text-surface-900">
                        <History className="text-surface-400" />
                        Últimos escaneos
                    </div>
                    <p className="m-0 mt-0.5 mb-3 text-xs text-surface-500">Fotos reales de anaquel para informar la edición del catálogo</p>
                    <div className="space-y-2">
                        {recentScans.map((scan) => (
                            <div
                                key={scan._id}
                                className="flex cursor-pointer items-center gap-3 rounded-lg border border-surface-200 p-2 transition-colors hover:bg-surface-50"
                                onClick={() => onRecordClick?.(scan)}>
                                <AuditImageThumb record={scan} size={48} />
                                <div className="min-w-0 flex-1">
                                    <div className="truncate text-sm font-medium text-surface-900">{scan.storeId?.name || '—'}</div>
                                    <div className="text-xs text-surface-500 tabular-nums">
                                        {scan.userId?.name || '—'} · {fmtVZ(scan.createdAt)}
                                    </div>
                                </div>
                                <AuditStatusBadge type="image" status={scan.imageProcessingStatus} />
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </Drawer>
    );
}
